import React, {Fragment, useState, useCallback} from "react"
import momentTZ from "moment-timezone"
import {useScheduledRewardsQuery} from "@irati/wallet/hooks/rewards"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import Spacing from "@irati/ui/src/Spacing"
import {Hr} from "@irati/ui/src/Layout"
import Loading from "@irati/ui/src/Loading"
import groupByDate from "@irati/wallet/helpers/groupWalletItemsByDate"
import {FlexiListScheduledSortEnum, ListScheduledRewards} from "@irati/types"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import NoData from "../../../NoData"
import DateHeader from "../../DateHeader"
import IntlMarkdown from "../../../styled/IntlMarkdown"
import {EditModal} from "./EditModal"
import {ScheduledReward} from "./ScheduledReward"

const modalStates = ["closed", "edit", "deleted", "complete", "scheduledNow"] as const
type ModalStates = (typeof modalStates)[number]

export const ScheduledRewards = injectIntl(function ScheduledRewardsComp({formatMessage}) {
  const [state, setState] = useState<ModalStates>("closed")
  const [viewedReward, setViewedReward] = useState<ListScheduledRewards>()
  const {load, data, refetch} = useScheduledRewardsQuery(
    {
      input: {
        sortBy: FlexiListScheduledSortEnum.ScheduledAtAsc
      }
    },
    {fetchPolicy: "cache-and-network"}
  )
  const closeModal = useCallback(() => setState("closed"), [])

  const rewardsByDate = data && data.length && groupByDate([], data.map(calculateTimestamp))

  return (
    <>
      <Loading load={load}>
        {rewardsByDate ? (
          rewardsByDate.results.map(({data: rewards, date}, i) => (
            <Fragment key={date.toString()}>
              {i === 0 ? <Spacing marginTop={2} /> : <Hr />}

              <DateHeader date={date} />

              {rewards.map((reward) => (
                <ScheduledReward
                  onEdit={() => {
                    setState("edit")
                    setViewedReward(reward)
                  }}
                  onDelete={() => {
                    setState("deleted")
                    refetch()
                  }}
                  key={reward.uuid}
                  reward={reward}
                  formatMessage={formatMessage}
                />
              ))}
            </Fragment>
          ))
        ) : (
          <Spacing marginTop={2}>
            <NoData useCardStyle text={formatMessage({id: "wallet.reward.noScheduledRewards.text"})} />
          </Spacing>
        )}
      </Loading>
      {state === "edit" && (
        <EditModal
          show
          onHide={() => setState("closed")}
          onComplete={(isScheduledNow) => {
            refetch()
            setState(isScheduledNow ? "scheduledNow" : "complete")
          }}
          reward={viewedReward!}
        />
      )}
      <GenericModal small show={state === "scheduledNow"} onHide={closeModal} hideActions>
        <IntlMarkdown id="wallet.statementItem.adminReward.scheduledReward.popup.scheduled" />
        <Button primary small full marginTop={2} onClick={closeModal}>
          <FormattedMessage id="platform.cta.ok" />
        </Button>
      </GenericModal>

      <GenericModal small show={state === "complete"} onHide={closeModal} hideActions>
        <IntlMarkdown id="wallet.statementItem.adminReward.scheduledReward.popup.updated" />
        <Button primary small full marginTop={2} onClick={closeModal}>
          <FormattedMessage id="platform.cta.ok" />
        </Button>
      </GenericModal>

      <GenericModal small show={state === "deleted"} onHide={closeModal} hideActions>
        <IntlMarkdown id="wallet.statementItem.adminReward.scheduledReward.popup.deleted" />
        <Button primary small full marginTop={2} onClick={closeModal}>
          <FormattedMessage id="platform.cta.ok" />
        </Button>
      </GenericModal>
    </>
  )
})

function calculateTimestamp(data: ListScheduledRewards) {
  const {when, originalTimezone} = data.scheduledFor
  const timestamp = momentTZ.tz(when, originalTimezone).toISOString()
  return {
    ...data,
    createdAt: timestamp
  }
}
